import { displayDate } from "../formatting/displayDate";
import type { SupportedLocale } from "../formatting/displayDate";

export type LeafEntry = { path: string[]; value: unknown };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

// A human label for an indexed entity, used both to show what an id in a tool's args refers to
// (ConfirmationCard) and to label an entity highlight card (entityHighlights). Shared here so both
// read the same field priority — never duplicate this list.
export const ENTITY_LABEL_KEYS = ["supplierName", "title", "name", "subject", "referenceId"] as const;

export const entityLabel = (entity: Record<string, unknown> | undefined): string => {
  if (!entity) {
    return "";
  }
  for (const key of ENTITY_LABEL_KEYS) {
    const value = entity[key];
    if (typeof value === "string" && value) {
      return value;
    }
  }
  return "";
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const moneyAmount = (value: Record<string, unknown>): number | undefined =>
  typeof value.amount === "number" ? value.amount : typeof value.value === "number" ? value.value : undefined;

const isMoneyShape = (value: Record<string, unknown>): boolean =>
  moneyAmount(value) !== undefined && typeof value.currency === "string";

export const hasLeafValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  return value != null;
};

// A tool's body args mirror its OpenAPI schema, so update-style fields nest arbitrarily deep
// (e.g. counterparty.counterparty.organizationEntryId). Flatten to one entry per leaf so the card
// can render a row per actual value instead of a JSON blob. Money shapes ({amount|value, currency})
// stay a single leaf — they render as one composite "amount currency" row, not two.
export const flattenLeaves = (path: string[], value: unknown): LeafEntry[] => {
  if (isPlainObject(value)) {
    if (isMoneyShape(value)) {
      return hasLeafValue(moneyAmount(value)) ? [{ path, value }] : [];
    }
    return Object.entries(value).flatMap(([key, nested]) => flattenLeaves([...path, key], nested));
  }
  return hasLeafValue(value) ? [{ path, value }] : [];
};

const PATH_LABEL_KEYS: Record<string, string> = {
  "startDate.date": "Start date",
  "endDate.date": "End date",
};

const LEAF_LABEL_KEYS: Record<string, string> = {
  answer: "Answer",
  title: "Title",
  description: "Description",
  internalNote: "Internal note",
  winningBidIds: "Winning bids",
  estimatedValue: "Estimated value",
  eventValue: "Estimated value",
  contractNature: "Contract type",
  organizationId: "Counterparty",
  organizationEntryId: "Counterparty",
  amount: "Value",
  hasNoEndDate: "No end date",
  personId: "Responsible",
};

// Returns a label KEY, not display text — callers look this up in a literal t(...) map (see
// ConfirmationCard's useLeafLabel) so langen can still statically extract every translation key.
export const leafLabelKey = (path: string[]): string => {
  const joined = path.join(".");
  const lastSegment = path[path.length - 1];
  return PATH_LABEL_KEYS[joined] ?? LEAF_LABEL_KEYS[lastSegment] ?? lastSegment;
};

export type FormatLeafOptions = {
  locale: SupportedLocale;
  yesLabel: string;
  noLabel: string;
  resolveEntityName: (id: string) => string | undefined;
};

export const formatLeafValue = (entry: LeafEntry, opts: FormatLeafOptions): string => {
  const { value } = entry;
  if (isPlainObject(value) && isMoneyShape(value)) {
    const amount = moneyAmount(value) ?? 0;
    return `${amount.toLocaleString("nb-NO")} ${value.currency as string}`;
  }
  if (typeof value === "boolean") {
    return value ? opts.yesLabel : opts.noLabel;
  }
  if (typeof value === "number") {
    return value.toLocaleString("nb-NO");
  }
  if (typeof value === "string") {
    const key = entry.path[entry.path.length - 1];
    if (key.endsWith("Id")) {
      return opts.resolveEntityName(value) ?? value;
    }
    if (ISO_DATE.test(value)) {
      return displayDate(value, opts.locale);
    }
    return value;
  }
  return value == null ? "" : String(value);
};
